import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type SkillLevel = "curious" | "tinkerer" | "builder" | "pro";
export type PrimaryGoal = "learn" | "build" | "compare" | "browse";

export interface UserPreferences {
  user_id: string;
  skill_level: SkillLevel;
  primary_goal: PrimaryGoal | null;
  onboarded_at: string | null;
  weekly_digest_opt_in: boolean;
  last_digest_sent_at: string | null;
  mention_sound_enabled: boolean;
  mention_browser_notifications_enabled: boolean;
  voice_default_muted: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Reads + writes the current user's preferences row. The row is created
 * lazily on first save (no upfront insert during signup) so anonymous
 * browsing stays free of side-effects.
 */
export function useUserPreferences() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["user-preferences", userId],
    enabled: !!userId,
    queryFn: async (): Promise<UserPreferences | null> => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      return (data as UserPreferences | null) ?? null;
    },
  });

  const upsert = useMutation({
    mutationFn: async (patch: {
      skill_level?: SkillLevel;
      primary_goal?: PrimaryGoal | null;
      onboarded_at?: string | null;
      weekly_digest_opt_in?: boolean;
      mention_sound_enabled?: boolean;
      mention_browser_notifications_enabled?: boolean;
      voice_default_muted?: boolean;
    }) => {
      if (!userId) throw new Error("Not signed in");
      // Upsert on user_id so the first save creates the row.
      const { data, error } = await supabase
        .from("user_preferences")
        .upsert(
          { user_id: userId, ...patch },
          { onConflict: "user_id" },
        )
        .select()
        .single();
      if (error) throw error;
      return data as UserPreferences;
    },
    onSuccess: (row) => {
      qc.setQueryData(["user-preferences", userId], row);
    },
  });

  return {
    preferences: query.data ?? null,
    isLoading: query.isLoading,
    needsOnboarding:
      !!user && query.isFetched && (query.data?.onboarded_at ?? null) === null,
    save: upsert.mutateAsync,
    isSaving: upsert.isPending,
  };
}
